import { IDeviceStatus } from '../interfaces/types.ts'
import { DateTime } from 'luxon'
import authStore from '../store/authStore.ts'

export const WakeReasons: string[] = [
    'Sleep canceled due to power cycle',
    'Wake from one time sleep',
    'Sleep canceled by user',
    'Wake from repeat sleep',
    'Sleep canceled due to low battery',
]

export const getWakeReasonText = (wakeReason?: string | number | null): string | null => {
    if (wakeReason === undefined || wakeReason === null) return null
    const index = typeof wakeReason === 'string' ? parseInt(wakeReason, 10) : wakeReason
    if (isNaN(index) || index < 0 || index >= WakeReasons.length) return null
    return WakeReasons[index]
}

export const hasScheduledSleep = (status: IDeviceStatus): boolean => {
    return status.sleepStart > 0 && status.sleepDuration > 0
}

export const isSleepDisabled = (status: IDeviceStatus): boolean => {
    return !status.powerOn || status.sleeping
}

export const isSleepFeatureEnabled = (account: any): boolean => {
    const envEnabled = import.meta.env.VITE_ENABLE_SLEEP_MODE === 'true'
    return envEnabled || account?.useAdvancedFeatures === true
}

export const calculateDurationMinutes = (startTime: number, endTime: number): number => {
    if (endTime > startTime) {
        return (endTime - startTime) / 60
    }
    // Handle overnight wrap-around (24h = 86400 seconds)
    return (86400 - startTime + endTime) / 60
}

export const calculateEndTime = (sleepStart: number, sleepDuration: number): number => {
    // sleepDuration is in minutes, sleepStart is in seconds
    return sleepStart + sleepDuration * 60
}

/**
 * Convert form time strings (HH:MM 24h) to device sleep settings.
 * For one-time: startEpoch = today's date + start time as unix timestamp
 * For repeating: startEpoch = just the time-of-day in seconds
 * Both use the user's configured timezone.
 */
export const convertToSleepCommand = (startTime: string, endTime: string, repeat: boolean): { startEpoch: number, duration: number } => {
    const zone = authStore.getState().timezone.value
    const [startH, startM] = startTime.split(':').map(Number)
    const [endH, endM] = endTime.split(':').map(Number)

    if (!repeat) {
        // One-time: use today's date + time as full unix epoch
        const now = DateTime.now().setZone(zone)
        let startDt = now.set({ hour: startH, minute: startM, second: 0, millisecond: 0 })
        let endDt = now.set({ hour: endH, minute: endM, second: 0, millisecond: 0 })

        // If end is before start, end is next day
        if (endDt <= startDt) {
            endDt = endDt.plus({ days: 1 })
        }

        const startEpoch = Math.floor(startDt.toSeconds())
        const duration = Math.floor((endDt.toSeconds() - startDt.toSeconds()) / 60)
        return { startEpoch, duration }
    } else {
        // Repeating: just time-of-day in seconds from midnight
        const startSeconds = startH * 3600 + startM * 60
        const endSeconds = endH * 3600 + endM * 60
        let duration = (endSeconds - startSeconds) / 60
        if (duration <= 0) {
            duration = (86400 - startSeconds + endSeconds) / 60
        }
        return { startEpoch: startSeconds, duration }
    }
}
